export const MARKDOWN_FORMATS = [
  "bold",
  "italic",
  "inline-code",
  "link",
  "heading-1",
  "heading-2",
  "heading-3",
  "bullet-list",
  "numbered-list",
  "task-list",
  "quote",
] as const;

export type MarkdownFormat = (typeof MARKDOWN_FORMATS)[number];
export type InlineMarkdownFormat = Extract<
  MarkdownFormat,
  "bold" | "italic" | "inline-code" | "link"
>;
export type LineMarkdownFormat = Exclude<MarkdownFormat, InlineMarkdownFormat>;

export interface InlineFormatResult {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

export function isInlineMarkdownFormat(format: MarkdownFormat): format is InlineMarkdownFormat {
  return format === "bold"
    || format === "italic"
    || format === "inline-code"
    || format === "link";
}

export function formatInlineText(
  selectedText: string,
  format: InlineMarkdownFormat,
): InlineFormatResult {
  if (format === "link") {
    if (selectedText) {
      const text = `[${selectedText}](https://)`;
      return {
        text,
        selectionStart: selectedText.length + 3,
        selectionEnd: text.length - 1,
      };
    }
    return { text: "[link text](https://)", selectionStart: 1, selectionEnd: 10 };
  }

  const placeholder = format === "bold"
    ? "bold text"
    : format === "italic"
      ? "italic text"
      : "code";
  const delimiter = format === "bold"
    ? "**"
    : format === "italic"
      ? "_"
      : selectedText.includes("`") ? "``" : "`";
  const content = selectedText || placeholder;
  return {
    text: `${delimiter}${content}${delimiter}`,
    selectionStart: delimiter.length,
    selectionEnd: delimiter.length + content.length,
  };
}

function lineHasFormat(line: string, format: LineMarkdownFormat): boolean {
  const content = line.replace(/^[\t ]*/, "");
  switch (format) {
    case "heading-1": return /^#\s+/.test(content);
    case "heading-2": return /^##\s+/.test(content);
    case "heading-3": return /^###\s+/.test(content);
    case "bullet-list": return /^[-+*]\s+(?!\[[ xX]\]\s+)/.test(content);
    case "numbered-list": return /^\d+[.)]\s+/.test(content);
    case "task-list": return /^[-+*]\s+\[[ xX]\]\s+/.test(content);
    case "quote": return /^>\s+/.test(content);
  }
}

function removeLinePrefix(line: string): { indent: string; content: string } {
  const indent = line.match(/^[\t ]*/)?.[0] ?? "";
  const content = line.slice(indent.length).replace(
    /^(?:#{1,6}|[-+*](?:\s+\[[ xX]\])?|\d+[.)]|>)\s+/,
    "",
  );
  return { indent, content };
}

function linePrefix(format: LineMarkdownFormat, number: number): string {
  switch (format) {
    case "heading-1": return "# ";
    case "heading-2": return "## ";
    case "heading-3": return "### ";
    case "bullet-list": return "- ";
    case "numbered-list": return `${number}. `;
    case "task-list": return "- [ ] ";
    case "quote": return "> ";
  }
}

export function formatMarkdownLines(text: string, format: LineMarkdownFormat): string {
  if (text === "") return linePrefix(format, 1);
  const lines = text.split("\n");
  const contentLines = lines.filter((line) => line.trim() !== "");
  const removeOnly = contentLines.length > 0
    && contentLines.every((line) => lineHasFormat(line, format));
  let numberedIndex = 1;

  return lines.map((line) => {
    if (line.trim() === "") return line;
    const { indent, content } = removeLinePrefix(line);
    if (removeOnly) return `${indent}${content}`;

    const prefix = linePrefix(format, numberedIndex);
    if (format === "numbered-list") numberedIndex += 1;
    return `${indent}${prefix}${content}`;
  }).join("\n");
}
